import React, { useEffect, useState } from "react";
import localeText from "../../../../locale";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useToggle } from "../../../../hooks/useToggle";
import { InputNumber } from "primereact/inputnumber";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import PhysicalStoreService from "../../../../service/PhysicalStoreService";
import { QRCodeCanvas } from "qrcode.react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatDate } from "../../../../utilities/formatDate";
import { formatPrice } from "../../../../utilities/formatPrice";

const StoreTransactions = ({ user, currency }) => {
  const [transactionValue, setTransactionValue] = useState();
  const [isLoadingCreateTransaction, setIsLoadingCreateTransaction] = useState(false);
  const [transactionObj, setTransactionObj] = useState();
  const [isLoadingTransaction, setIsLoadingTransaction] = useState(false);
  const [isLoadingGetAllPayments, setIsLoadingGetAllPayments] = useState(true);
  const [transactions, setTransactions] = useState();

  const { showSuccessToast, showErrorToast } = useToastContext();

  const {
    value: isOpenCreateTransactionModal,
    setAsTrue: openCreateTransactionModal,
    setAsFalse: closeCreateTransactionModal,
  } = useToggle();

  const {
    value: isOpenQrCodeTransactionModal,
    setAsTrue: openOpenQrCodeTransactionModal,
    setAsFalse: closeOpenQrCodeTransactionModal,
  } = useToggle();

  const physicalStoreService = new PhysicalStoreService();

  const handleCreateTransaction = async () => {
    setIsLoadingCreateTransaction(true);

    try {
      const res = await physicalStoreService.createTransaction({
        amount: transactionValue,
        user: user.username,
      });

      if (res.messageCode === 200000) {
        setTransactionValue(undefined);
        closeCreateTransactionModal();
        setTransactionObj(res.result);
        handleOpenQrCodeTransactionModal(res.result);
        return;
      }

      showErrorToast({ code: res.messageCode });
    } catch (error) {
      console.error("Error creating transaction:", error);
    } finally {
      setIsLoadingCreateTransaction(false);
    }
  };

  const handleOpenQrCodeTransactionModal = async (transaction) => {
    if (typeof transaction === "object") {
      openOpenQrCodeTransactionModal();
      return;
    }

    setIsLoadingTransaction(true);

    openOpenQrCodeTransactionModal();
    try {
      const res = await physicalStoreService.getPayment(transaction);

      if (res.messageCode !== 200001) {
        showErrorToast({ code: res.messageCode });
        return;
      }

      setTransactionObj(res.result);
    } catch (error) {
      console.error("Error fetching transaction:", error);
    } finally {
      setIsLoadingTransaction(false);
    }
  };

  const getAllPayments = async () => {
    setIsLoadingGetAllPayments(true);

    try {
      const res = await physicalStoreService.getAllPayments();

      if (res.messageCode !== 200001) {
        showErrorToast({ code: res.messageCode });
        return;
      }

      setTransactions(res.result);
    } catch (error) {
      console.error("Error fetching transaction:", error);
    } finally {
      setIsLoadingGetAllPayments(false);
    }
  };

  const handleCloseQrCodeTransactionModal = () => {
    setTransactionObj(undefined);
    closeOpenQrCodeTransactionModal();
  };

  useEffect(() => {
    const verifyIfTransactionIsPaid = async () => {
      if (isOpenQrCodeTransactionModal && transactionObj?.id) {
        const res = await physicalStoreService.getPayment(transactionObj?.id);

        if (res.messageCode !== 200001) {
          showErrorToast({ code: res.messageCode });
          return;
        }

        if (res.result?.dateTimePayment) {
          setTransactionObj(res.result);
          handleCloseQrCodeTransactionModal();
          showSuccessToast({ detail: "transactionDone" });
          await getAllPayments();
        }
      }
    };

    const interval = setInterval(() => {
      verifyIfTransactionIsPaid();
    }, 3000);

    if (!isOpenQrCodeTransactionModal) clearInterval(interval);

    return () => clearInterval(interval); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenQrCodeTransactionModal, transactionObj]);

  useEffect(() => {
    if (user) getAllPayments(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <>
      <Dialog
        header={localeText("transaction")}
        visible={isOpenCreateTransactionModal}
        className="w-30rem m-2"
        onHide={() => {
          if (!isLoadingCreateTransaction) closeCreateTransactionModal();
        }}
        draggable={false}
      >
        <div className="flex flex-column gap-4">
          <div className="flex flex-column gap-2">
            <label htmlFor="amount" className="font-bold">
              {localeText("amount")}
            </label>
            <InputNumber
              value={transactionValue}
              onChange={(e) => setTransactionValue(e.value)}
              min={0}
              mode="currency"
              currency={currency}
              placeholder={`${currency} 0,00`}
            />
          </div>

          <div className="flex justify-content-end">
            <Button
              label={localeText("create")}
              icon="pi pi-check"
              loading={isLoadingCreateTransaction}
              onClick={handleCreateTransaction}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header={`${localeText("qrCodeOfTransaction")}`}
        visible={isOpenQrCodeTransactionModal}
        className="w-30rem m-2"
        onHide={handleCloseQrCodeTransactionModal}
        draggable={false}
      >
        {isLoadingTransaction ? (
          <div className="flex justify-content-center align-items-center">
            <i className="pi pi-spin pi-spinner text-600 text-3xl my-2"></i>
          </div>
        ) : (
          <>
            {transactionObj?.id && (
              <div className="flex flex-column justify-content-center align-items-center gap-2">
                <div>
                  <QRCodeCanvas value={`${transactionObj.id}`} size={200} />
                </div>
                <p className="m-0">{localeText("awaitingPayment")}...</p>
                <p>
                  {currency} {formatPrice(transactionObj.amountPaid)}
                </p>
              </div>
            )}
          </>
        )}
      </Dialog>

      <div className="card border-1 border-300">
        <div className="flex align-items-center justify-content-between">
          <h3 className="my-4">{localeText("transactions")}</h3>

          <Button
            icon="pi pi-plus"
            onClick={openCreateTransactionModal}
            label={localeText("transaction")}
          />
        </div>

        <DataTable
          value={transactions}
          paginator
          loading={isLoadingGetAllPayments}
          responsiveLayout="scroll"
          className="mt-4 mb-6"
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate={localeText("showingFirstToLast")}
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
        >
          <Column
            header={"ID"}
            body={(variation) => {
              return (
                <div className="flex">
                  <p>{variation.id}</p>
                </div>
              );
            }}
          />

          <Column
            header={localeText("username")}
            body={(variation) => {
              return (
                <div className="flex">
                  <p>{variation.seller.username}</p>
                </div>
              );
            }}
          />

          <Column
            header={localeText("value")}
            body={(variation) => {
              return (
                <div className="flex">
                  <p>
                    {currency} {formatPrice(variation.amountPaid)}
                  </p>
                </div>
              );
            }}
          />

          <Column
            header={localeText("adminBackofficeDateCreation")}
            body={(variation) => {
              return <p>{formatDate(variation.dateTime)}</p>;
            }}
          />

          <Column
            header={localeText("payer")}
            body={(variation) => {
              return <p>{variation?.buyer?.username ? variation?.buyer?.username : ""}</p>;
            }}
          />

          <Column
            header={localeText("paymentDate")}
            body={(variation) => {
              return (
                <p>{variation.dateTimePayment ? formatDate(variation.dateTimePayment) : ""}</p>
              );
            }}
          />

          <Column
            header={localeText("actions")}
            body={(variation) => {
              return (
                <div>
                  <Button
                    icon="pi pi-qrcode"
                    disabled={variation?.dateTimePayment}
                    onClick={() => handleOpenQrCodeTransactionModal(variation.id)}
                  />
                </div>
              );
            }}
          />
        </DataTable>
      </div>
    </>
  );
};

export default StoreTransactions;
